import type { VisitorStorage } from "@/lib/visitor-storage";
import type { WidgetFeatures } from "@/lib/widget-config";
import { isTabEnabled, routeToTab, type WidgetRoute } from "@/lib/widget-route";

/** A saved place older than this is ignored (the visitor has moved on). */
export const PLACE_TTL_MS = 30 * 60 * 1000;

/** Where the visitor was: whether the panel was open, and the route (tab + drill-in). */
export interface SavedPlace {
  open: boolean;
  route: WidgetRoute;
}

const key = (publicKey: string | null) => `wexio:place:${publicKey ?? "default"}`;
const MAX_ORIGIN_DEPTH = 6;

const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

/** Keeps only the defined entries, so a saved route carries no `undefined` noise. */
function compact<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

/**
 * A clean `WidgetRoute` from untrusted data, or `null`. Only known fields survive: the transient ones
 * (`pendingLatest`, `keyAs`, `welcomeSlotId`) are never restored, and a route still resolving the latest
 * thread has no place to remember yet. When `features` is given, a route on a disabled tab is rejected
 * (an `origin` on a disabled tab is dropped instead).
 */
export function sanitizeRoute(
  raw: unknown,
  features: WidgetFeatures | null,
  depth = 0,
): WidgetRoute | null {
  if (!raw || typeof raw !== "object" || depth > MAX_ORIGIN_DEPTH) return null;
  const r = raw as Record<string, unknown>;
  const origin = () => {
    const o = sanitizeRoute(r.origin, features, depth + 1);
    return o ? { origin: o } : {};
  };
  let route: WidgetRoute | null = null;
  switch (r.kind) {
    case "home":
    case "messages":
    case "help":
    case "news":
    case "profile":
      route = { kind: r.kind };
      break;
    case "messages-thread": {
      if (r.pendingLatest) return null;
      const threadKey = r.threadKey === null ? null : str(r.threadKey);
      route = compact({ kind: "messages-thread" as const, chatId: str(r.chatId), threadKey });
      break;
    }
    case "help-category": {
      const categoryId = str(r.categoryId);
      if (!categoryId) return null;
      route = compact({
        kind: "help-category" as const,
        categoryId,
        categoryName: str(r.categoryName),
        categoryDescription: str(r.categoryDescription),
        categoryArticleCount: num(r.categoryArticleCount),
        ...origin(),
      });
      break;
    }
    case "help-article": {
      const slug = str(r.slug);
      if (!slug) return null;
      route = compact({
        kind: "help-article" as const,
        slug,
        from: r.from === "search" ? ("search" as const) : ("category" as const),
        fromCategoryId: str(r.fromCategoryId),
        locale: str(r.locale),
        ...origin(),
      });
      break;
    }
    case "news-article": {
      const id = str(r.id);
      if (!id) return null;
      route = { kind: "news-article", id, ...origin() };
      break;
    }
    default:
      return null;
  }
  if (!route || (features && !isTabEnabled(routeToTab(route), features))) return null;
  return route;
}

/** Remembers the place (with a timestamp). Never throws; an unsavable route keeps the previous entry. */
export function savePlace(
  storage: VisitorStorage,
  publicKey: string | null,
  place: SavedPlace,
  now = Date.now(),
): void {
  const route = sanitizeRoute(place.route, null);
  if (!route) return;
  try {
    storage.setItem(key(publicKey), JSON.stringify({ at: now, open: place.open, route }));
  } catch {
    // Blocked storage: no restore, no crash.
  }
}

/** The remembered place if it is fresh and valid for the resolved `features`; otherwise `null`. */
export function readPlace(
  storage: VisitorStorage,
  publicKey: string | null,
  features: WidgetFeatures,
  now = Date.now(),
): SavedPlace | null {
  try {
    const raw = storage.getItem(key(publicKey));
    if (!raw) return null;
    const data: unknown = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    const d = data as Record<string, unknown>;
    if (typeof d.at !== "number" || now - d.at >= PLACE_TTL_MS || now < d.at - 60_000) return null;
    const route = sanitizeRoute(d.route, features);
    return route ? { open: d.open === true, route } : null;
  } catch {
    return null;
  }
}
